/*
 * Voucher / discount code field (snippets/rs-cart-discount.liquid), in the cart drawer and on the
 * cart page (RS cart footer).
 * - Codes are saved on the cart with /cart/update.js ("discount", comma separated), so Shopify
 *   carries them to checkout whether they were typed in the drawer or on the cart page.
 * - The cart's full code list comes from /cart.js (discount_codes): Liquid only knows the codes
 *   that give a discount now, so free shipping codes (worked out at checkout) and codes that no
 *   longer apply are added here as extra pills, and are never dropped when another code is added.
 * - A code that Shopify marks as not applicable is taken off the cart again and an error is shown.
 * - After a change the drawer (.drawer__inner) and the cart page sections are rendered again with
 *   the same sections Dawn uses, so totals, free shipping bar and pills are current everywhere.
 * - Shopify keeps the codes on the cart when it is emptied, so the next product added would get
 *   the discount again. When the cart becomes empty (Dawn publishes cart-update after removing a
 *   line) the codes are taken off the cart.
 */
if (!customElements.get('rs-cart-discount')) {
  let drawerOpen = false; // drawer field opened by the customer, kept across Dawn's re-renders
  let codesCache = null; // { at, codes: [{ code, applicable }] }
  let codesPromise = null;

  const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

  const fetchCodes = (maxAge = 0) => {
    if (codesCache && Date.now() - codesCache.at <= maxAge) return Promise.resolve(codesCache.codes);
    if (!codesPromise) {
      codesPromise = fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          codesCache = { at: Date.now(), codes: cart.discount_codes || [] };
          return codesCache.codes;
        })
        .finally(() => (codesPromise = null));
    }
    return codesPromise;
  };

  // Sections to render again after a change, like Dawn's cart.js / cart-drawer.js.
  const sectionsToRender = (withBubble = false) => {
    const list = [];
    if (withBubble && document.getElementById('cart-icon-bubble')) {
      list.push({
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
        target: () => document.getElementById('cart-icon-bubble'),
      });
    }
    // RS main cart marks its regions with data-rs-selector (top bar, lines, summary, one section);
    // Dawn's main-cart-items and RS cart footer use their first .js-contents.
    ['rs-mc-top', 'main-cart-items', 'main-cart-footer'].forEach((id) => {
      const el = document.getElementById(id);
      const section = el?.dataset.id || el?.closest('[data-section-id]')?.dataset.sectionId;
      if (!section) return;
      const selector = el.dataset.rsSelector || '.js-contents';
      list.push({
        section,
        selector,
        target: () => document.getElementById(id)?.querySelector(selector),
      });
    });
    if (document.querySelector('cart-drawer #CartDrawer .drawer__inner')) {
      list.push({
        section: 'cart-drawer',
        selector: '.drawer__inner',
        target: () => document.querySelector('cart-drawer #CartDrawer .drawer__inner'),
      });
    }
    return list;
  };

  const renderSections = (html, sections) => {
    if (!html) return;
    sections.forEach((s) => {
      const target = s.target();
      const source = html[s.section] && new DOMParser().parseFromString(html[s.section], 'text/html');
      const inner = source?.querySelector(s.selector);
      if (target && inner) target.innerHTML = inner.innerHTML;
    });
  };

  // Shared with assets/rs-cart-variant.js (change size in the cart).
  const sectionIds = (sections) => [...new Set(sections.map((s) => s.section))];
  window.rsCart = { sectionsToRender, renderSections, sectionIds };

  const updateCart = (codes, sections) =>
    fetch(routes.cart_update_url, {
      ...fetchConfig('json'),
      body: JSON.stringify({
        discount: codes.join(','),
        sections: [...new Set(sections.map((s) => s.section))],
        sections_url: window.location.pathname,
      }),
    }).then((r) => {
      if (!r.ok) throw new Error(`cart update ${r.status}`);
      return r.json();
    });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cart = event?.cartData;
      if (!cart || cart.item_count !== 0) return;
      if (Array.isArray(cart.discount_codes) && !cart.discount_codes.length) return;
      updateCart([], [])
        .then(() => (codesCache = { at: Date.now(), codes: [] }))
        .catch((e) => console.error(e));
    });
  }

  // Codes Liquid shows as pills in a rendered section (they give a discount right now).
  const liquidCodes = (html) => {
    if (!html) return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('rs-cart-discount [data-rs-cd-code]'), (el) => el.dataset.rsCdCode);
  };

  customElements.define(
    'rs-cart-discount',
    class RsCartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.toggle = this.querySelector('.rs-cd__toggle');
        this.panel = this.querySelector('.rs-cd__panel');
        this.message = this.querySelector('.rs-cd__message');
        this.list = this.querySelector('.rs-cd__pills');

        if (this.toggle && this.panel) {
          this.setOpen(drawerOpen);
          this.toggle.addEventListener('click', () => {
            drawerOpen = !drawerOpen;
            this.setOpen(drawerOpen);
            if (drawerOpen) this.input?.focus();
          });
        }
        // In RS main cart the field sits inside Dawn's <cart-items>, which treats every "change"
        // event as a quantity change: keep the voucher's events to itself.
        this.addEventListener('change', (event) => event.stopPropagation());
        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.apply(this.input.value);
        });
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('.rs-cd__remove');
          if (remove) {
            event.preventDefault();
            this.remove(remove.dataset.code);
          }
        });

        this.watchVisible();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      setOpen(open) {
        this.toggle.setAttribute('aria-expanded', String(open));
        this.panel.hidden = !open;
      }

      // Extra pills need /cart.js: asked only when the field is on screen (drawer open, cart page).
      watchVisible() {
        if (!('IntersectionObserver' in window)) return this.refreshPills(1000);
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.observer.disconnect();
            this.refreshPills(1000);
          }
        });
        this.observer.observe(this);
      }

      refreshPills(maxAge) {
        return fetchCodes(maxAge)
          .then((codes) => this.renderExtraPills(codes))
          .catch(() => {});
      }

      renderExtraPills(codes) {
        if (!this.list) return;
        this.list.querySelectorAll('.rs-cd__pill--extra').forEach((el) => el.remove());
        const shown = Array.from(this.list.querySelectorAll('[data-rs-cd-code]'), (el) => el.dataset.rsCdCode);
        const tpl = this.querySelector('template.rs-cd__pill-template');
        if (!tpl) return;
        codes
          .filter((c) => !shown.some((s) => same(s, c.code)))
          .forEach((c) => {
            const pill = tpl.content.firstElementChild.cloneNode(true);
            pill.classList.add('rs-cd__pill--extra');
            if (!c.applicable) pill.classList.add('is-inactive');
            pill.querySelector('.rs-cd__code').textContent = c.code;
            const note = pill.querySelector('.rs-cd__note');
            note.textContent = c.applicable ? this.dataset.atCheckout : this.dataset.inactive;
            const btn = pill.querySelector('.rs-cd__remove');
            btn.dataset.code = c.code;
            btn.setAttribute('aria-label', (this.dataset.removeLabel || '').replace('[code]', c.code));
            this.list.appendChild(pill);
          });
        this.list.hidden = !this.list.children.length;
      }

      showMessage(type, code) {
        if (!this.message) return;
        const text = (this.dataset[`msg${type}`] || '').replace('[code]', code || '');
        this.message.textContent = text;
        this.message.className = `rs-cd__message rs-cd__message--${type.toLowerCase()}`;
        this.message.hidden = !text;
      }

      setBusy(busy) {
        this.toggleAttribute('aria-busy', busy);
        this.querySelectorAll('button').forEach((b) => (b.disabled = busy));
        if (this.input) this.input.readOnly = busy;
      }

      async apply(raw) {
        const code = (raw || '').trim();
        if (!code || this.hasAttribute('aria-busy')) return;
        this.setBusy(true);
        this.showMessage('None', '');
        try {
          const current = (await fetchCodes(0)).map((c) => c.code);
          if (current.some((c) => same(c, code))) {
            this.showMessage('Already', code);
            return;
          }
          const sections = sectionsToRender();
          let data = await updateCart([...current, code], sections);
          const entry = (data.discount_codes || []).find((c) => same(c.code, code));
          let result = 'ok';
          if (!entry || !entry.applicable) {
            // Not valid for this cart: take it off again so it does not linger on the cart.
            data = await updateCart(current, sections);
            result = 'Error';
          } else {
            // Applicable but not among Liquid's pills: a free shipping code, worked out at checkout.
            const html = Object.values(data.sections || {}).find((h) => h && h.includes('<rs-cart-discount'));
            if (html && !liquidCodes(html).some((c) => same(c, code))) result = 'Shipping';
          }
          codesCache = { at: Date.now(), codes: data.discount_codes || [] };
          this.render(data.sections, sections, result, code);
        } catch (e) {
          console.error(e);
          this.showMessage('Failed', code);
        } finally {
          if (this.isConnected) this.setBusy(false);
        }
      }

      async remove(code) {
        if (!code || this.hasAttribute('aria-busy')) return;
        this.setBusy(true);
        try {
          const current = (await fetchCodes(0)).map((c) => c.code).filter((c) => !same(c, code));
          const sections = sectionsToRender();
          const data = await updateCart(current, sections);
          codesCache = { at: Date.now(), codes: data.discount_codes || [] };
          this.render(data.sections, sections, null, code);
        } catch (e) {
          console.error(e);
          this.showMessage('Failed', code);
        } finally {
          if (this.isConnected) this.setBusy(false);
        }
      }

      render(html, sections, result, code) {
        const context = this.dataset.context;
        renderSections(html, sections);
        // The new elements (same place as this one) show the message and get the focus back.
        const fresh = Array.from(document.querySelectorAll(`rs-cart-discount[data-context="${context}"]`));
        fresh.forEach((el) => {
          el.renderExtraPills?.(codesCache.codes);
          if (result && result !== 'ok') el.showMessage(result, code);
          else if (result === 'ok') el.showMessage('Applied', code);
        });
        const first = fresh[0];
        if (first) (first.input && !first.panel?.hidden ? first.input : first.querySelector('button'))?.focus();
      }
    }
  );
}
